import { useCallback, useEffect, useRef, useState } from 'react';
import type { GameRoom, GameSession, Player } from '@goodtime/shared';
import { fetchPlayer, getOrCreatePlayer } from '../api/players';
import { createRoom, subscribeToRoom } from '../api/rooms';
import { startSession } from '../api/sessions';

export type ConnectionStatus = 'connecting' | 'live' | 'disconnected' | 'error';

export interface ServerConnection {
  player: Player | null;
  room: GameRoom | null;
  /** Phase D.1 — active slot session. Set once {@link startSession}
   * resolves; null while connecting or on error. */
  session: GameSession | null;
  tickCount: number;
  subscribers: number;
  status: ConnectionStatus;
  errorMessage: string | null;
  /** Fetch the player's features and their slots again (a world switched on
   *  or off on the server since the last fetch). Also done when the tab
   *  comes back into view. */
  refreshPlayer: () => void;
}

/**
 * Establish the GTC server connection on mount: resolve the persisted
 * anonymous player (created once on first launch, reused thereafter so the
 * player's wallet + profile backup survive relaunches), spin up a slot
 * room, subscribe to its SSE event stream. Cleans up the EventSource on
 * unmount.
 *
 * <p>Lifted out of {@link ../App App} so the connection lifecycle is
 * testable and reusable from any host that wants to talk to the GTC server.
 * The {@link ConnectionStatus} surface is the same: connecting / live /
 * disconnected (auto-reconnecting) / error.
 */
export function useServerConnection(): ServerConnection {
  const [player, setPlayer] = useState<Player | null>(null);
  const [room, setRoom] = useState<GameRoom | null>(null);
  const [session, setSession] = useState<GameSession | null>(null);
  const [tickCount, setTickCount] = useState(0);
  const [subscribers, setSubscribers] = useState(0);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const playerRef = useRef<Player | null>(null);
  playerRef.current = player;
  const refreshing = useRef(false);

  // Only the features and their slots are taken from the new fetch, and only
  // when they changed, so nothing else re-renders for it.
  const refreshPlayer = useCallback(() => {
    const id = playerRef.current?.id;
    if (!id || refreshing.current) return;
    refreshing.current = true;
    void fetchPlayer(id)
      .then((fresh) => {
        setPlayer((p) =>
          p && p.id === fresh.id && !sameGrants(p, fresh)
            ? { ...p, features: fresh.features, featureThemes: fresh.featureThemes }
            : p,
        );
      })
      .catch(() => undefined)
      .finally(() => {
        refreshing.current = false;
      });
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshPlayer();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshPlayer]);

  useEffect(() => {
    let cancelled = false;
    let eventSource: EventSource | null = null;

    void (async () => {
      try {
        const newPlayer = await getOrCreatePlayer();
        if (cancelled) return;
        setPlayer(newPlayer);

        const newRoom = await createRoom('slot');
        if (cancelled) return;
        setRoom(newRoom);

        // Phase D.1 — kick off a slot session immediately. The slot
        // scene needs the session id to call POST /sessions/{id}/spin.
        // If session start fails, the rest of the UI still works
        // (offline / local-only mode) — we just don't set session.
        try {
          const newSession = await startSession({
            playerId: newPlayer.id,
            gameRoomId: newRoom.id,
          });
          if (!cancelled) setSession(newSession);
        } catch (sessionErr) {
          // Non-fatal: surface as a warning, leave session null so the
          // slot scene falls back to local spins.
          console.warn('[server] failed to start session, falling back to local', sessionErr);
        }

        eventSource = subscribeToRoom(newRoom.id, {
          onWelcome: (event) => {
            setSubscribers(event.subscribers);
            setStatus('live');
          },
          onTick: (event) => {
            setTickCount((c) => c + 1);
            setSubscribers(event.subscribers);
          },
          onError: () => {
            // Browser EventSource auto-reconnects; flag the dip but don't
            // tear the world down — onWelcome bumps status back to 'live'
            // when the reconnection succeeds.
            setStatus('disconnected');
          },
        });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Connection failed';
        setErrorMessage(message);
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      eventSource?.close();
    };
  }, []);

  return { player, room, session, tickCount, subscribers, status, errorMessage, refreshPlayer };
}

/** The same features and slots for both. */
function sameGrants(a: Player, b: Player): boolean {
  return (
    JSON.stringify(a.features ?? null) === JSON.stringify(b.features ?? null) &&
    JSON.stringify(a.featureThemes ?? null) === JSON.stringify(b.featureThemes ?? null)
  );
}
